import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Result, Space, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import SignupLayout from './SignupLayout';
import { signupPalette, signupGradient } from './signupTheme';
import { useSignupApi } from '../../../api/auth/signup';
import { useAuth } from '../../../context/AuthContext';

const { Text } = Typography;

/**
 * Écran du lien magique reçu par email.
 *
 * C'est ici que le parent (ou le conjoint) choisit son mot de passe : le compte
 * est activé dans la foulée, puis la session est ouverte automatiquement — le
 * parcours d'inscription se termine donc dans l'application, pas sur un
 * formulaire de connexion.
 */
const SetPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const signupApi = useSignupApi();
    const { login } = useAuth();

    const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <SignupLayout title={t('setPassword.invalidTitle', 'Lien invalide')}>
                <Result
                    status="warning"
                    title={t('setPassword.invalidTitle', 'Lien invalide')}
                    subTitle={t(
                        'setPassword.invalidSubtitle',
                        'Ce lien est incomplet ou a expiré. Demandez-en un nouveau depuis « Mot de passe oublié ».'
                    )}
                    extra={
                        <Button type="primary" onClick={() => navigate('/login')}>
                            {t('signup.actions.login', 'Se connecter')}
                        </Button>
                    }
                />
            </SignupLayout>
        );
    }

    if (done) {
        return (
            <SignupLayout title={t('setPassword.doneTitle', 'Mot de passe enregistré')}>
                <Result
                    status="success"
                    title={t('setPassword.doneTitle', 'Mot de passe enregistré')}
                    subTitle={t('setPassword.doneSubtitle', 'Votre compte est activé. Connectez-vous pour commencer.')}
                    extra={
                        <Button type="primary" onClick={() => navigate('/login')}>
                            {t('signup.actions.login', 'Se connecter')}
                        </Button>
                    }
                />
            </SignupLayout>
        );
    }

    const onFinish = async (values: { password: string; confirmPassword: string }) => {
        setError(undefined);
        setSubmitting(true);
        try {
            const account = await signupApi.setPassword(token, values.password);
            // Le compte vient d'être activé : on enchaîne sur une vraie session
            // plutôt que de renvoyer l'utilisateur vers la page de connexion.
            const connected = await login(account.login, values.password);
            if (connected) {
                navigate('/');
                return;
            }
            setDone(true);
        } catch (err: any) {
            const data = err?.response?.data;
            setError(
                (typeof data === 'string' && data) ||
                    data?.message ||
                    t('setPassword.error', 'Le mot de passe n’a pas pu être enregistré. Le lien a peut-être expiré.')
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <SignupLayout
            title={t('setPassword.title', 'Choisissez votre mot de passe')}
            subtitle={t('setPassword.subtitle', 'Dernière étape avant d’accéder à votre espace famille.')}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {error && <Alert type="error" showIcon message={error} />}

                <Form layout="vertical" requiredMark={false} onFinish={onFinish}>
                    <Form.Item
                        name="password"
                        label={<Text style={{ fontWeight: 500 }}>{t('setPassword.password', 'Mot de passe')}</Text>}
                        rules={[
                            { required: true, message: t('setPassword.passwordRequired', 'Saisissez un mot de passe') },
                            { min: 6, message: t('setPassword.passwordMin', 'Au moins 6 caractères') },
                        ]}
                    >
                        <Input.Password
                            autoFocus
                            size="large"
                            autoComplete="new-password"
                            prefix={<LockOutlined style={{ color: signupPalette.textMuted }} />}
                            style={{ borderRadius: 12, padding: '12px 16px' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        label={<Text style={{ fontWeight: 500 }}>{t('setPassword.confirm', 'Confirmer le mot de passe')}</Text>}
                        rules={[
                            { required: true, message: t('setPassword.confirmRequired', 'Confirmez votre mot de passe') },
                            ({ getFieldValue }) => ({
                                validator: (_, value) =>
                                    !value || getFieldValue('password') === value
                                        ? Promise.resolve()
                                        : Promise.reject(
                                              new Error(t('setPassword.mismatch', 'Les mots de passe ne correspondent pas'))
                                          ),
                            }),
                        ]}
                    >
                        <Input.Password
                            size="large"
                            autoComplete="new-password"
                            prefix={<LockOutlined style={{ color: signupPalette.textMuted }} />}
                            style={{ borderRadius: 12, padding: '12px 16px' }}
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        loading={submitting}
                        style={{
                            height: 52,
                            borderRadius: 12,
                            border: 'none',
                            background: signupGradient,
                            fontWeight: 600,
                            fontSize: 16,
                        }}
                    >
                        {t('setPassword.submit', 'Activer mon compte')}
                    </Button>
                </Form>
            </Space>
        </SignupLayout>
    );
};

export default SetPassword;
